import React from 'react';
import { 
  Box, 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton,
  Container,
  Fade
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import OfflineStatusBar from './OfflineStatusBar';
import EmailVerificationBanner from './EmailVerificationBanner';
import VerificationSuccessNotification from './VerificationSuccessNotification';
import ChatAssistant from './chat/ChatAssistant';
import DemoGuide from './DemoGuide';
import NotificationBell from './NotificationBell';

// Import styling from our design system
import { colors, shadows } from '../styles/styleConstants';

/**
 * Common layout component for pages in the mobile app
 * Provides a consistent header with back navigation
 */
const Layout = ({ 
  title, 
  children, 
  onBack, 
  action, 
  disableBackButton = false,
  fullWidth = false,
  searchComponent = null,
  customTitleComponent = null // New prop for custom title component
}) => {
  const navigate = useNavigate();
  
  // Default back action if none is provided
  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };
  
  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        minHeight: '100vh',
        height: 'calc(100vh - 72px)', // Account for bottom nav height (68px + 4px buffer)
        overflow: 'hidden',
        bgcolor: colors.backgroundColor
      }}
    >
      {/* App Bar - fixed position so it stays at the top when scrolling */}
      <AppBar 
        position="fixed" 
        elevation={0}
        sx={{
          bgcolor: customTitleComponent ? colors.slateBlue || colors.secondary : colors.softWhite,
          color: customTitleComponent ? colors.lightGray : colors.darkSlateBlue,
          borderBottom: customTitleComponent ? 'none' : `1px solid ${colors.divider}`,
          boxShadow: customTitleComponent ? 'none' : shadows.sm,
          overflow: 'visible',
        }}
      >
        <Toolbar sx={{ pr: 2, pl: 1 }}>
          {!disableBackButton && (
            <IconButton
              edge="start"
              aria-label="back"
              onClick={handleBack}
              sx={{ 
                mr: 0.5,
                color: customTitleComponent ? colors.lightGray : colors.darkSlateBlue,
                '&:hover': {
                  backgroundColor: customTitleComponent ? 'rgba(255, 255, 255, 0.1)' : 'rgba(94, 108, 132, 0.08)',
                }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {searchComponent ? (
              // Show search component instead of title when provided
              searchComponent
            ) : customTitleComponent ? (
              // Show custom title component if provided
              <Fade in={true} timeout={800}>
                {customTitleComponent}
              </Fade>
            ) : (
              // Default title and logo display
              <Fade in={true} timeout={800}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <img 
                    src="/logo192.png" 
                    alt="ClutterLogix" 
                    style={{ 
                      height: '32px', 
                      width: '32px', 
                      marginRight: '8px',
                      borderRadius: '6px',
                    }} 
                  />
                  <Typography 
                    variant="h6" 
                    component="div" 
                    sx={{ 
                      fontWeight: 600,
                      fontFamily: "'Poppins', sans-serif",
                      color: colors.darkSlateBlue,
                      letterSpacing: '-0.01em',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {title}
                  </Typography>
                </Box>
              </Fade>
            )}
          </Box>
          
          {/* Action buttons - right after title */}
          {action && (
            <Box sx={{ overflow: 'visible', ml: 1 }}>
              {action}
            </Box>
          )}
          
          {/* Spacer to push notification bell to the right */}
          <Box sx={{ flexGrow: 1 }} />
          
          {/* Notification Bell */}
          <NotificationBell />
        </Toolbar>
      </AppBar>
      
      {/* Toolbar placeholder to prevent content from hiding under the AppBar */}
      <Toolbar />
      
      {/* Main Content */}
      <Container 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          py: 3,
          px: fullWidth ? 0 : 2,
          maxWidth: '100%',
          bgcolor: colors.backgroundColor,
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none'
        }}
        maxWidth={fullWidth ? false : 'lg'}
        disableGutters={fullWidth}
      >
        {/* Email Verification Banner */}
        <EmailVerificationBanner />
        
        {children}
      </Container>
      
      {/* Global Offline Status Indicator */}
      <OfflineStatusBar />
      
      {/* Verification Success Notification */}
      <VerificationSuccessNotification />
      
      {/* Chat Assistant */}
      <ChatAssistant />
      
      {/* Demo Guide for demo mode users */}
      <DemoGuide />
    </Box>
  );
};

export default Layout;
